/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as EntitledAssets from 'core/store/ducks/entitlements/assets/entitledAssets';
import { Rail } from 'ui/components';
import ColdStartRail from 'ui/components/Rail/ColdStartRail';
import Constants from 'configs/Constants';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import translate from 'services/i18n/translate';
import { RailMetaData } from 'ui/pages/myTv/MainPage';
import RouterUtils from 'services/utils/RouterUtils';
import { PrivateRoutePath } from 'routes/paths';
import { Asset, EntryPoint } from '@wit/vtv-x-foundation-lib';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import * as BadgesStoreUtils from 'store/utils/badgesStoreUtils';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import { StoreState } from 'store/store';
import { useRouter } from '../../../hooks/useRouter';

interface Props {
  rail: RailMetaData;
}

export default function MyTVPurchasesAndRentals({ rail }: Props) {
  const dispatch = useDispatch();
  const history = useHistoryWithState();
  const router = useRouter();

  const onHeaderClick = () => {
    history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.MYTV_RAIL, [rail.item.id]));
  };

  const onCardClick = (asset: Asset, menuItemId?: number) => {
    router.push({ asset, entryPoint: EntryPoint.PURCHASES, menuItemId });
  };

  useEffect(() => {
    dispatch(EntitledAssets.ActionCreators.getEntitlementsAssets());
  }, [dispatch]);

  const isProcessed = useSelector(EntitledAssets.Selectors.isProcessed);
  const entitledAssets = useSelector((state: StoreState) =>
    BadgesStoreUtils.Selector.getAssetsWithBadges(
      state,
      BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.RENTAR_AND_PURCHASES_RAIL,
      EntitledAssets.Selectors.getAssets,
      EntryPoint.PURCHASES
    )
  );

  if (!isProcessed) {
    return <></>;
  }

  return isProcessed && entitledAssets.length === 0 ? (
    <ColdStartRail
      railId={rail.item.id}
      railName={translate['firstTimeUse.purchases.label']}
      railBackgroundImageName={RailBackgroundImageName.PURCHASES}
      coldStartTitle={translate['firstTimeUse.purchases.title']}
      railDescription={translate['firstTimeUse.purchases.subtitle']}
      className="no-background"
    />
  ) : (
    <Rail
      railId={rail.item.id}
      menuItemId={rail.item.id}
      className="no-background"
      railName={translate['firstTimeUse.purchases.label']}
      channelId={rail.item.channelId}
      assets={entitledAssets}
      hasMore={entitledAssets.length > Constants.MAX_ASSETS_PER_RAIL}
      onHeaderClick={onHeaderClick}
      onCardClick={onCardClick}
    />
  );
}
